"use client";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import RadioButton from "../../atoms/radioButton";
import { COUNTRIES, SELECT_TIME_SLOT } from "../../../constants/common/common";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { getProductDeliveryType, processDeliveryDate, updateCurrency } from "../../../utils/common";
import { shallowEqual, useSelector } from "react-redux";
import { timeSlotStringAndCount } from "../../../utils/common/timeSlot";
import { calculateNextCycleSlot, calenderIsTodayOrTomorrowDate } from "../../../utils/common/deliveryDate";

/**
 * Represents the available time slots for delivery selection.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array} props.timeSlots - An array containing the available time slots for a particular product.
 * @param {Function} props.onTimeSlotSelection - Callback function to handle the selection of a specific time slot.
 * @returns {React.ReactElement} A React element displaying the available time slots.
 */
const TimeSlots = ({
  timeSlots,
  onTimeSlotSelection,
  popupCleverTapData,
  cartCleverTapData,
  setUserSelectedTimeSlotId,
  currentItem,
  isCart,
  selectedCurrency,
  price,
  shippingMethodName,
  selectedDate,
}) => {
  const { trackEvent } = useAnalytics();
  const { selectedIntLocation, selectedDomesticLocation } = useSelector(
    (state) => ({
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
      selectedDomesticLocation: state?.locationLock?.selectedDomesticLocation,
    }),
    shallowEqual,
  );

  const pinCode = selectedDomesticLocation?.pincode;
  const address = selectedDomesticLocation?.address;

  /**
   * This function handles a click event on a time slot i.e. it updates the selected time slot state with the provided time.
   *
   * @function
   * @param {string} time - The start time of the selected time slot.
   */
  const onHandleTimeSlotClick = (event, slot, formatTime) => {
    onTimeSlotSelection(event, { timeSlots: slot, formatTime });
    if (isCart) {
      const deliveryTimeSlot = event?.target?.innerText;
      setUserSelectedTimeSlotId(deliveryTimeSlot === formatTime);
      trackEvent(
        cleverTapCommonEvents.deliverySlotSelected,
        { ...cartCleverTapData, delivery_slot: formatTime || "" },
        ["clevertap"],
      );
    } else {
      trackEvent(
        cleverTapCommonEvents.deliverySlotSelected,
        { ...popupCleverTapData, delivery_slot: formatTime || "" },
        ["clevertap"],
      );
    }
  };

  useEffect(() => {
    if (timeSlots) {
      const { timeSlotsString, timeSlotCount } = timeSlotStringAndCount(timeSlots);
      const { isDateToday, isDateTomorrow } = calenderIsTodayOrTomorrowDate(selectedDate);
      const productDeliveryType = getProductDeliveryType(currentItem?.primaryCategoryId);
      const isInternationalBasedOnClickedItem =
        (currentItem?.catalogId || currentItem?.productCatalogId) !== COUNTRIES.INDIA;
      const urlPath = currentItem?.urlIdentifier;

      const cleverTapDataSlotsViewed = {
        page_name: urlPath ? urlPath : cleverTapCommonWords.CART,
        page_type: urlPath ? cleverTapCommonWords.PDP : cleverTapCommonWords.CART,
        product_name: currentItem?.productName,
        pid: currentItem?.productId,
        product_category: currentItem?.primaryCategoryId || currentItem?.primaryProductCategoryId,
        product_price: currentItem?.productPrice?.price || currentItem?.mainProduct?.price,
        delivery_type: productDeliveryType,
        pincode: isInternationalBasedOnClickedItem ? "" : pinCode,
        area: isInternationalBasedOnClickedItem ? "" : address,
        system_pincode: isInternationalBasedOnClickedItem ? "" : pinCode,
        country: currentItem?.catalogId || currentItem?.productCatalogId,
        city: isInternationalBasedOnClickedItem ? selectedIntLocation?.location : "",
        is_international: isInternationalBasedOnClickedItem,
        avialable_delivery_slots: timeSlotsString,
        avialable_delivery_slot_count: timeSlotCount,
        delivery_shipping: shippingMethodName,
        delivery_shipping_fee: updateCurrency(selectedCurrency, price),
        date_of_delivery: processDeliveryDate(selectedDate || ""),
        is_date_today: isDateToday,
        is_date_tomorrow: isDateTomorrow,
      };

      trackEvent(cleverTapCommonEvents.deliverySlotsViewed, cleverTapDataSlotsViewed, ["clevertap"]);
    }
  }, [timeSlots]);

  return (
    <div className="mt-16">
      <span className={`ml-24 pl-8 text-12 font-500 text-grey-400`}>{SELECT_TIME_SLOT}</span>
      {timeSlots?.map((slot, index) => {
        let updatedSlot = { ...slot };
        if (slot?.isDynamic === "Y" && slot?.leadTime) {
          const { startTime: start, endTime: end } = calculateNextCycleSlot(30 * 60 * 1000, slot.leadTime);
          updatedSlot = {
            ...updatedSlot,
            startTime: start,
            endTime: end,
          };
        }
        let startTime = updatedSlot?.startTime.substring(0, 5);
        let endTime = updatedSlot?.endTime.substring(0, 5);
        let timeSlot = `${startTime} - ${endTime} Hrs`;
        return (
          <div className="ml-24 flex cursor-pointer items-start justify-between gap-5 py-10 pl-3 " key={index}>
            <div
              className="flex w-[100%] items-center gap-10"
              onClick={(event) => onHandleTimeSlotClick(event, updatedSlot, timeSlot)}
            >
              <RadioButton onChange={() => null} isChecked={false} label="" id={timeSlot} name={"timeSlot"} />
              <div>
                <span className="text-14 font-500 not-italic text-grey-400">{timeSlot}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

TimeSlots.propTypes = {
  timeSlots: PropTypes.arrayOf(PropTypes.object).isRequired,
  onTimeSlotSelection: PropTypes.func.isRequired,
  popupCleverTapData: PropTypes.object,
  cartCleverTapData: PropTypes.object,
  setUserSelectedTimeSlotId: PropTypes.func,
  currentItem: PropTypes.object,
  isCart: PropTypes.bool,
  selectedCurrency: PropTypes.object,
  price: PropTypes.string,
  shippingMethodName: PropTypes.string,
  selectedDate: PropTypes.string,
};

export default TimeSlots;
